import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { Box, TableCell, Typography } from "@mui/material";
import getLocale from "../../../helpers/getLocale";
import { SEARCH_GROUP_NAME } from "./constants";
import { GroupedTableColumn } from "./GroupedTable";
import GroupedTableSearchInput from "./search/GroupedTableSearchInput";
import { GroupedTableSearchProps } from "./search/useGroupedTableSearch";
import { CheckboxPadded, GroupHeader } from "./styled";

interface Props {
  columns: GroupedTableColumn[];
  open: boolean;
  setOpen: () => void;
  rowTitle: {
    name: string;
    year?: string;
    secondaryTextKey?: string;
  };
  translation: string;
  disableSelectItems: boolean;
  search?: GroupedTableSearchProps;
  disableSelectGroupItems: boolean;
  selectGroup: (checkBoxSelected: boolean) => void;
  isGroupChecked: boolean;
}

export const TableGroupToggle = ({
  columns,
  open,
  setOpen,
  rowTitle,
  translation,
  disableSelectItems,
  search,
  disableSelectGroupItems,
  selectGroup,
  isGroupChecked,
}: Props) => {
  const locale = getLocale();
  const { t } = useTranslation(translation);
  const { name, year, secondaryTextKey } = rowTitle;

  const groupDateRegex = /^\d{4}-(0?[1-9]|1[012])$/;
  const displayYear = !!year ? year : "";
  const categoryName = groupDateRegex.test(name)
    ? `${format(new Date(`${name}-01`), "LLLL", {
        locale,
      }).toLocaleUpperCase()} ${displayYear}`
    : t(name).toLocaleUpperCase();

  // TODO: check with disabledSelectedItems
  // add one column for checkboxes if selecting items are not disabled
  const getColSpan = () => {
    const colSpan = disableSelectItems ? columns.length : columns.length + 1;
    if (search) {
      return colSpan + 1;
    }
    return colSpan;
  };

  const isSearchGroup = rowTitle.name === SEARCH_GROUP_NAME;

  return (
    <GroupHeader
      sx={{ position: "relative" }}
      disablePadding={isSearchGroup && !open}
    >
      <TableCell
        onClick={() => setOpen()}
        colSpan={getColSpan()}
        sx={{
          paddingLeft: "0px",
          borderBottom: isSearchGroup ? "none" : "1px solid #E5E5E5",
        }}
      >
        <GroupedTableSearchInput
          isSearchGroup={isSearchGroup}
          search={search as GroupedTableSearchProps}
          open={open}
        />
        {!isSearchGroup && (
          <Box display="flex" alignItems="end">
            <Box display="flex" alignItems="end" gap="18px">
              {!disableSelectGroupItems && (
                <CheckboxPadded
                  checked={isGroupChecked}
                  onChange={(e) => selectGroup(e.target.checked)}
                  sx={{ paddingLeft: 0.6 }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <IconButton
                size="small"
                onClick={() => setOpen()}
                sx={{ padding: 0, margin: 0, marginLeft: 1, color: "#4A1D9C" }}
              >
                {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </IconButton>
              <Typography
                whiteSpace="nowrap"
                color="#4A1D9C"
                fontWeight="700"
                variant="header"
                lineHeight={1.65}
              >
                {categoryName}
              </Typography>
              {secondaryTextKey && (
                <Typography variant="secondary" lineHeight={1.65}>
                  {t(secondaryTextKey)}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </TableCell>
    </GroupHeader>
  );
};
